// searchEngine.ts - Client-side search engine implementation

import { useFieldConfigStore } from "../stores/fieldConfigStore";
import { NewSearchResult, SearchResultSpaceInfo } from "../types/aiChatClient";
import { SearchCriteria, SearchCriteriaItem } from "../types/search_engine";

export interface SearchableData {
  [key: string]: any;
  detail?: {
    [key: string]: any;
  };
  detailTabs?: Array<{
    detail: {
      [key: string]: any;
    };
  }>;
  spaceNumber?: string;
  spaceHeader?: string;
}

export interface EnhancedSearchResult extends NewSearchResult {
  targetPlanInfo: any;
}

export class ClientSearchEngine {
  private data: SearchableData[];
  private distanceData: any;

  constructor(data: SearchableData[] = [], distanceData: any = {}) {
    this.data = data;
    this.distanceData = distanceData;
  }

  /**
   * Execute search based on SearchCriteria and return NewSearchResult
   */
  searchWithResult(searchCriteria: SearchCriteria): NewSearchResult {
    const filteredData = this.search(searchCriteria);
    const targetPlanInfo = this.findTargetPlanInfo(searchCriteria);

    // Convert to SearchResultSpaceInfo format and include distance data
    const searchResults: SearchResultSpaceInfo[] = filteredData.map((item) => {
      const spaceID = this.extractSpaceID(item);
      const area = this.extractArea(item);
      const tenant = this.extractTenant(item);
      const distance = this.calculateDistance(item, targetPlanInfo);

      const totalRentPSF = this.extractTotalRentPSF(item);
      const leaseEndDate = this.extractLeaseEndDate(item);
      const totalRent = this.extractTotalRent(item);

      // Add missing fields
      const totalSales = this.getNestedValue(item, "Total Sales") || "unknown";
      const totalSalesPSF = this.getNestedValue(item, "Total Sales PSF") || "unknown";

      return {
        spaceID,
        area,
        tenant,
        distance,
        totalRentPSF,
        leaseEndDate,
        totalRent,
        totalSales,
        totalSalesPSF,
      };
    });

    return {
      targetPlanInfo,
      searchResults,
    };
  }

  /**
   * Execute search based on SearchCriteria
   */
  search(searchCriteria: SearchCriteria): SearchableData[] {
    if (
      !searchCriteria.searchCriteria ||
      searchCriteria.searchCriteria.length === 0
    ) {
      return this.data;
    }

    return this.data.filter((item) => {
      const results = searchCriteria.searchCriteria.map((criteria) =>
        this.applyCriteria(item, criteria)
      );

      // Apply AND/OR logic
      return searchCriteria.logic === "OR"
        ? results.some((r) => r)
        : results.every((r) => r);
    });
  }

  /**
   * Find target plan info from search criteria
   */
  private findTargetPlanInfo(searchCriteria: SearchCriteria): any {
    // Look for target entity in proximity searches
    const proximityCriteria = searchCriteria.searchCriteria.find(
      (criteria) => criteria.operator === "PROXIMITY"
    );

    if (proximityCriteria?.referenceEntity) {
      const targetName = proximityCriteria.referenceEntity.toLowerCase();
      return this.findTargetDynamically(targetName);
    }

    // Look for specific entity names in other criteria
    for (const criteria of searchCriteria.searchCriteria) {
      const targetName = String(criteria.value).toLowerCase();
      const target = this.findTargetDynamically(targetName, criteria.field);
      if (target) return target;
    }

    return undefined;
  }

  /**
   * Dynamically find target item by looping through all object keys and values recursively
   * @param searchValue - The value to search for (case-insensitive)
   * @param field - The field to search in
   * @param exactMatch - Whether to require exact match or allow partial match
   * @returns The found item or null
   */
  private findTargetDynamically(
    searchValue: string,
    field: string = "",
    exactMatch: boolean = false
  ): any {
    const normalizedSearchValue = searchValue.toLowerCase();

    for (const item of this.data) {
      const found = this.searchInObjectRecursively(
        item,
        normalizedSearchValue,
        field,
        exactMatch
      );
      if (found) {
        return item; // Return the parent item that contains the matching value
      }
    }

    return null;
  }

  /**
   * Recursively search through an object's properties and nested objects
   * @param obj - The object to search in
   * @param searchValue - The value to search for (normalized to lowercase)
   * @param exactMatch - Whether to require exact match or allow partial match
   * @param visited - Set to track visited objects and avoid circular references
   * @returns Boolean indicating if the value was found
   */
  private searchInObjectRecursively(
    obj: any,
    searchValue: string,
    field: string = "",
    exactMatch: boolean = false,
    visited: Set<any> = new Set()
  ): boolean {
    // Prevent circular references
    if (visited.has(obj)) {
      return false;
    }
    visited.add(obj);

    // Handle null or undefined
    if (obj === null || obj === undefined) {
      return false;
    }

    // Handle primitive values
    if (typeof obj !== "object") {
      const stringValue = String(obj).toLowerCase();
      return exactMatch
        ? stringValue === searchValue
        : stringValue.includes(searchValue);
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      for (const item of obj) {
        if (
          this.searchInObjectRecursively(
            item,
            searchValue,
            field,
            exactMatch,
            visited
          )
        ) {
          return true;
        }
      }
      return false;
    }

    // Handle objects - loop through all keys
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        const value = obj[key];

        // Check if the current value matches (for string/primitive values)
        if (typeof value === "string" || typeof value === "number") {
          const stringValue = String(value).toLowerCase();
          const matches = exactMatch
            ? stringValue === searchValue
            : stringValue.includes(searchValue);

          if (matches) {
            return true;
          }
        }

        // If value is an object or array, search recursively
        if (typeof value === "object" && value !== null) {
          if (
            this.searchInObjectRecursively(
              value,
              searchValue,
              field,
              exactMatch,
              visited
            )
          ) {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Calculate distance between two spaces using distanceData
   */
  private calculateDistance(item: SearchableData, targetPlanInfo: any): number {
    if (!targetPlanInfo || !this.distanceData?.spaces) return 0;

    const targetSpaceNumber = targetPlanInfo.spaceNumber
      ?.replaceAll(".", "_")
      .replaceAll(" ", "_");
    const itemSpaceNumber = item.spaceNumber
      ?.replaceAll(".", "_")
      .replaceAll(" ", "_");

    if (!targetSpaceNumber || !itemSpaceNumber) return 0;

    // Find distance data for target space
    const distanceInfo = this.distanceData.spaces.find(
      (distInfo: any) => distInfo.spaceNumber === targetSpaceNumber
    );

    if (!distanceInfo?.distances) return 0;

    // Find distance to the item space
    const distanceRecord = distanceInfo.distances.find(
      (dist: any) => dist.spaceNumber === itemSpaceNumber
    );

    return distanceRecord?.Distance || 0;
  }

  private extractTotalRentPSF(item: SearchableData): string {
    return this.getNestedValue(item, "Total Rent PSF") || "unknown";
  }

  private extractLeaseEndDate(item: SearchableData): string {
    return this.getNestedValue(item, "Lease End Date") || "unknown";
  }

  private extractTotalRent(item: SearchableData): string {
    return this.getNestedValue(item, "Total Rent") || "unknown";
  }

  /**
   * Extract space ID from various possible fields
   */
  private extractSpaceID(item: SearchableData): string {
    return (
      item.spaceNumber ||
      item["Space #"] ||
      this.getNestedValue(item, "Space #") ||
      this.getNestedValue(item, "Lease ID") ||
      "unknown"
    );
  }

  /**
   * Extract area from various possible fields and handle unit conversion
   */
  private extractArea(item: SearchableData): number {
    const areaValue =
      item.GLA ||
      this.getNestedValue(item, "GLA") ||
      this.getNestedValue(item, "Actual Footprint SF") ||
      "0";

    if (!areaValue) return 0;

    let areaString = String(areaValue);

    // Remove common units and formatting
    areaString = areaString
      .replace(/\s*(m2|m²|m\u00B2|sq\.?\s*ft\.?|square\s*feet)/i, "")
      .replace(/[,$\s]/g, "");

    const parsed = parseFloat(areaString);
    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Extract tenant name from various possible fields
   */
  private extractTenant(item: SearchableData): string {
    return (
      item.spaceHeader ||
      item.DBA ||
      this.getNestedValue(item, "DBA") ||
      this.getNestedValue(item, "Tenant Name") ||
      "Unknown Tenant"
    );
  }

  /**
   * Apply a single search criteria to an item
   */
  private applyCriteria(
    item: SearchableData,
    criteria: SearchCriteriaItem
  ): boolean {
    // Handle proximity criteria with actual distance calculations
    if (criteria.operator === "PROXIMITY") {
      return this.applyProximityCriteriaWithDistance(item, criteria);
    }

    const value = this.getNestedValue(item, criteria.field);
    if (value === undefined || value === null) return false;

    const stringValue = String(value).toLowerCase();
    const searchValue = String(criteria.value).toLowerCase();

    switch (criteria.operator) {
      case "ILIKE":
        return stringValue.includes(searchValue);

      case "LIKE":
        return String(value).includes(String(criteria.value));

      case "EQUAL":
        return stringValue === searchValue;

      case "LOWER_THAN":
        return this.parseNumeric(value) < this.parseNumeric(criteria.value);

      case "UPPER_THAN":
        return this.parseNumeric(value) > this.parseNumeric(criteria.value);

      case "BETWEEN":
        if (!criteria.value2) return false;
        const numValue = this.parseNumeric(value);
        const min = this.parseNumeric(criteria.value);
        const max = this.parseNumeric(criteria.value2);
        return numValue >= min && numValue <= max;

      case "IN":
        const values = Array.isArray(criteria.value)
          ? criteria.value
          : [criteria.value];
        return values.some((v) => stringValue === String(v).toLowerCase());

      default:
        return false;
    }
  }

  /**
   * Apply proximity criteria using actual distance data
   */
  private applyProximityCriteriaWithDistance(
    item: SearchableData,
    criteria: SearchCriteriaItem
  ): boolean {
    const { referenceEntity, referenceField, distance, distanceUnit } =
      criteria;

    if (!referenceEntity || !this.distanceData?.spaces) return false;

    // Find the reference entity (target)
    const targetName = referenceEntity.toLowerCase();
    const referenceItem = this.data.find((refItem) => {
      const refValue = this.getNestedValue(refItem, referenceField || "DBA");
      return refValue && String(refValue).toLowerCase().includes(targetName);
    });

    if (!referenceItem) {
      console.log(
        `⚠️ Reference entity "${referenceEntity}" not found in dataset`
      );
      return false;
    }

    // Calculate actual distance using distance data
    const actualDistance = this.calculateDistance(item, referenceItem);

    if (!distance || distance === "nearby") {
      // If no specific distance, consider anything in the same building as nearby
      return actualDistance > 0; // Distance > 0 means they are different spaces but connected
    }

    // Parse distance value and apply filter
    const maxDistance = this.parseNumeric(distance);

    // Convert distance units if needed (assume distance data is in consistent units)
    let distanceThreshold = maxDistance;
    if (distanceUnit === "m" || distanceUnit === "meters") {
      distanceThreshold = maxDistance * 3.28084; // Convert to feet if needed
    }

    return actualDistance <= distanceThreshold && actualDistance > 0;
  }

  /**
   * Apply proximity criteria (legacy method for backward compatibility)
   */
  private applyProximityCriteria(
    item: SearchableData,
    criteria: SearchCriteriaItem
  ): boolean {
    return this.applyProximityCriteriaWithDistance(item, criteria);
  }

  /**
   * Get nested value from object, including detailTabs structure
   */
  private getNestedValue(obj: SearchableData, path: string): any {
    // First check in detailTabs if it exists (common structure)
    if (
      obj.detailTabs &&
      Array.isArray(obj.detailTabs) &&
      obj.detailTabs.length > 0
    ) {
      const detailValue = obj.detailTabs[0].detail?.[path];
      if (detailValue !== undefined) return detailValue;
    }

    // Then check in detail if it exists
    if (obj.detail && obj.detail[path] !== undefined) {
      return obj.detail[path];
    }

    // Then check in main object
    if (obj[path] !== undefined) {
      return obj[path];
    }

    // Search in all sub-objects
    for (const key in obj) {
      if (
        typeof obj[key] === "object" &&
        obj[key] !== null &&
        !Array.isArray(obj[key])
      ) {
        const nested = this.getNestedValue(obj[key], path);
        if (nested !== undefined) return nested;
      }
    }

    return undefined;
  }

  /**
   * Parse numeric values (handles $, %, commas, units)
   */
  private parseNumeric(value: any): number {
    if (typeof value === "number") return value;

    const stringValue = String(value);
    // Remove $, %, commas, spaces, and common units
    const cleaned = stringValue.replace(
      /[$,%\s]|(m2|m²|m\u00B2|sq\.?\s*ft\.?|square\s*feet)/gi,
      ""
    );
    const parsed = parseFloat(cleaned);

    return isNaN(parsed) ? 0 : parsed;
  }

  /**
   * Update the data source
   */
  updateData(newData: SearchableData[], distanceData: any): void {
    this.data = newData;
    this.distanceData = distanceData;
  }

  /**
   * Get available fields from the data with field configuration information
   */
  getAvailableFields(): string[] {
    const fieldConfigStore = useFieldConfigStore.getState();
    const { fieldConfigs, isInitialized } = fieldConfigStore;

    if (isInitialized && fieldConfigs.length > 0) {
      // Return configured active fields with their types and formats
      return fieldConfigs
        .filter((config) => config.isActive)
        .sort((a, b) => a.priority - b.priority)
        .map((config) => {
          const typeInfo = config.fieldType.format
            ? `${config.fieldType.type}(${config.fieldType.format})`
            : config.fieldType.type;
          return `${config.fieldPath}:${typeInfo}`;
        });
    }

    // Fallback to auto-detection if not configured
    const fields = new Set<string>();

    const extractFields = (obj: any, prefix = "") => {
      for (const key in obj) {
        if (
          typeof obj[key] === "object" &&
          obj[key] !== null &&
          !Array.isArray(obj[key])
        ) {
          extractFields(obj[key], prefix ? `${prefix}.${key}` : key);
        } else {
          fields.add(prefix ? `${prefix}.${key}` : key);
        }
      }
    };

    this.data.forEach((item) => extractFields(item));
    return Array.from(fields).sort();
  }

  /**
   * Get formatted field configurations for AI consumption
   */
  getFieldConfigurationsForAI(): string {
    const fieldConfigStore = useFieldConfigStore.getState();
    const { fieldConfigs, isInitialized } = fieldConfigStore;

    if (!isInitialized || fieldConfigs.length === 0) {
      return "No field configurations available";
    }

    const activeConfigs = fieldConfigs.filter((config) => config.isActive);

    const configDescriptions = activeConfigs.map((config) => {
      let description = `${config.fieldPath} (${config.fieldType.type})`;

      if (config.fieldType.format) {
        description += ` format: ${config.fieldType.format}`;
      }

      if (config.fieldType.unit) {
        description += ` unit: ${config.fieldType.unit}`;
      }

      if (config.fieldType.description) {
        description += ` - ${config.fieldType.description}`;
      }

      return description;
    });

    return configDescriptions.join(", ");
  }
}

/**
 * Convert search results to the format expected by the chat system
 */
export function convertSearchResultsToNewSearchResult(
  searchResults: SearchableData[],
  searchCriteria: SearchCriteria,
  distanceData?: any
): NewSearchResult {
  const engine = new ClientSearchEngine(searchResults, distanceData);
  return engine.searchWithResult(searchCriteria);
}

// Create a singleton instance for the client-side search engine
export const clientSearchEngine = new ClientSearchEngine();
